<app-page-wrapper [title]="pageTitle()" [loading]="loadingData()" [error]="null">
  <ng-container *ngIf="!loadingData()">
    @if (ownerMode()) {
      <div class="mb-5">
        <p-card>
          <div class="calendar-owner-bar">
            <div class="calendar-owner-copy">
              <span class="calendar-eyebrow">Widok właściciela</span>
              <h2>Kalendarz dla wybranego użytkownika</h2>
              <p>
                Aktualnie przeglądasz:
                <strong>
                  {{ selectedUser()?.firstName ?? 'wybierz' }}
                  {{ selectedUser()?.lastName ?? 'użytkownika' }}
                </strong>
              </p>
            </div>

            <div class="calendar-owner-select">
              <label for="calendarOwnerUser">Użytkownik</label>
              <p-select
                inputId="calendarOwnerUser"
                [options]="ownerUserOptions()"
                optionLabel="fullName"
                optionValue="id"
                [ngModel]="selectedUserId()"
                (ngModelChange)="onOwnerUserChange($event)"
                placeholder="Wybierz użytkownika"
                appendTo="body"
                styleClass="w-full"
              >
                <ng-template let-user pTemplate="item">
                  {{ user.firstName }} {{ user.lastName }}
                </ng-template>
                <ng-template let-user pTemplate="selectedItem">
                  {{ user.firstName }} {{ user.lastName }}
                </ng-template>
              </p-select>
            </div>
          </div>
        </p-card>
      </div>
    }

    <div class="mb-5">
      <p-card>
        <div class="calendar-toolbar">
          <div class="calendar-toolbar-copy">
            <span class="calendar-eyebrow">Miesiąc</span>
            <h2>{{ monthLabel() }}</h2>
            <p>
              {{ monthEntryCount() }} pozycji czasu
              <span class="calendar-dot">•</span>
              {{ formatDuration(monthTotalDuration()) }}
            </p>
          </div>

          <div class="calendar-toolbar-actions">
            <div class="calendar-month-select">
              <p-select
                [options]="monthOptions"
                optionLabel="label"
                optionValue="value"
                [ngModel]="selectedMonth()"
                (ngModelChange)="onMonthChange($event)"
                appendTo="body"
                styleClass="w-full"
              ></p-select>
            </div>

            <div class="calendar-year-select">
              <p-select
                [options]="yearOptions()"
                [ngModel]="selectedYear()"
                (ngModelChange)="onYearChange($event)"
                appendTo="body"
                styleClass="w-full"
              ></p-select>
            </div>

            <p-button label="Dziś" outlined (onClick)="goToCurrentMonth()"></p-button>
            <p-button icon="pi pi-chevron-left" variant="outlined" (onClick)="goToPreviousMonth()"></p-button>
            <p-button icon="pi pi-chevron-right" variant="outlined" (onClick)="goToNextMonth()"></p-button>
          </div>
        </div>
      </p-card>
    </div>

    <div class="calendar-layout">
      <p-card class="calendar-grid-card">
        <div class="calendar-mobile-grid md:hidden">
          <div class="calendar-mobile-week" *ngFor="let week of calendarWeeks(); let weekIndex = index; trackBy: trackByWeek">
            <div class="calendar-mobile-week-header">
              <button
                type="button"
                class="calendar-mobile-week-toggle"
                (click)="toggleWeek(weekIndex)"
              >
                <i class="pi" [ngClass]="isWeekCollapsed(weekIndex) ? 'pi-chevron-right' : 'pi-chevron-down'"></i>
                <span>Tydzień {{ weekIndex + 1 }}</span>
              </button>
              <small>{{ formatWeekRange(week) }}</small>
            </div>

            <div *ngIf="!isWeekCollapsed(weekIndex)">
              <button
                type="button"
                class="calendar-mobile-day"
                *ngFor="let cell of week; trackBy: trackByDateKey"
                [class.calendar-mobile-day-outside]="!cell.inCurrentMonth"
                [class.calendar-mobile-day-today]="cell.isToday"
                [class.calendar-mobile-day-selected]="cell.isSelected"
                [class.calendar-mobile-day-has-entries]="cell.entryCount > 0"
                (click)="selectDay(cell)"
              >
                <div class="calendar-mobile-day-head">
                  <span class="calendar-day-number">{{ cell.date.getDate() }}</span>
                  <span class="calendar-mobile-day-label">{{ formatDayShort(cell) }}</span>
                </div>

                <div class="calendar-mobile-day-meta">
                  <span *ngIf="cell.entryCount > 0" class="calendar-day-count">
                    {{ cell.entryCount }} poz.
                  </span>
                  <span *ngIf="cell.entryCount > 0" class="calendar-day-duration">
                    {{ formatDuration(cell.totalDuration) }}
                  </span>
                  <span *ngIf="cell.entryCount === 0" class="calendar-day-empty">Brak pozycji</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div class="calendar-desktop-grid hidden md:grid">
          <div class="calendar-grid">
            <div class="calendar-weekday" *ngFor="let day of ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd']">
              {{ day }}
            </div>

            <button
              *ngFor="let cell of calendarDays(); trackBy: trackByDateKey"
              type="button"
              class="calendar-day"
              [class.calendar-day-outside]="!cell.inCurrentMonth"
              [class.calendar-day-today]="cell.isToday"
              [class.calendar-day-selected]="cell.isSelected"
              [class.calendar-day-has-entries]="cell.entryCount > 0"
              (click)="selectDay(cell)"
            >
              <div class="calendar-day-head">
                <span class="calendar-day-number">{{ cell.date.getDate() }}</span>
                <span *ngIf="cell.entryCount > 0" class="calendar-day-count">
                  {{ cell.entryCount }} poz.
                </span>
              </div>

              <div class="calendar-day-body">
                <span *ngIf="cell.entryCount > 0" class="calendar-day-duration">
                  {{ formatDuration(cell.totalDuration) }}
                </span>
                <span *ngIf="cell.entryCount === 0" class="calendar-day-empty">Brak pozycji</span>
              </div>
            </button>
          </div>
        </div>
      </p-card>

      <p-card class="calendar-day-panel">
        <div class="calendar-panel-header">
          <div>
            <span class="calendar-eyebrow">Wybrany dzień</span>
            <h3>{{ selectedDayLabel() }}</h3>
          </div>

          <div class="calendar-panel-stats" *ngIf="selectedDayEntries().length > 0; else emptyPanelStats">
            <span>{{ selectedDayEntries().length }} pozycji</span>
            <span>{{ formatDuration(selectedDayTotalDuration()) }}</span>
          </div>

          <ng-template #emptyPanelStats>
            <span class="calendar-panel-muted">Brak wpisów</span>
          </ng-template>

          <p-button
            label="Nowa"
            icon="pi pi-plus"
            size="small"
            severity="secondary"
            outlined
            styleClass="calendar-new-entry-button"
            (onClick)="openNewEntryForDay()"
          ></p-button>
        </div>

        <p-divider></p-divider>

        <div class="calendar-day-panel-body">
          @if (selectedDayGroups().length > 0) {
            <div class="calendar-group" *ngFor="let group of selectedDayGroups(); trackBy: trackByGroup">
              <div class="calendar-group-header">
                <button type="button" class="calendar-group-main" (click)="openDayGroup(group)">
                  <div class="calendar-group-copy">
                    <div class="calendar-group-title">
                      <span class="calendar-group-dot" [style.background-color]="group.color || 'var(--app-primary)'"></span>
                      <span>{{ group.workItemName ?? 'Bez nazwy' }}</span>
                    </div>
                    <span class="calendar-group-client">{{ group.clientName ?? 'Brak klienta' }}</span>
                  </div>

                  <div class="calendar-group-meta">
                    <span>{{ group.entries.length }} poz.</span>
                    <strong>{{ formatDuration(group.totalDuration) }}</strong>
                  </div>
                </button>

                <div class="calendar-group-actions">
                  <p-button
                    label="Nowa"
                    icon="pi pi-plus"
                    size="small"
                    severity="secondary"
                    outlined
                    styleClass="calendar-group-action-button"
                    (onClick)="openNewEntryForDay(group); $event.stopPropagation()"
                  ></p-button>
                  <button
                    type="button"
                    class="calendar-group-toggle"
                    (click)="toggleGroup(group.workItemId); $event.stopPropagation()"
                    [attr.aria-expanded]="!isGroupCollapsed(group.workItemId)"
                  >
                    <i class="pi" [ngClass]="isGroupCollapsed(group.workItemId) ? 'pi-chevron-down' : 'pi-chevron-up'"></i>
                  </button>
                </div>
              </div>

              <div class="calendar-entry-list" *ngIf="!isGroupCollapsed(group.workItemId)">
                <button
                  type="button"
                  class="calendar-entry-row"
                  *ngFor="let entry of group.entries; trackBy: trackByEntry"
                  (click)="openEntry(entry)"
                >
                  <div class="calendar-entry-main">
                    <span class="calendar-entry-time">{{ formatTimeRange(entry.start, entry.end) }}</span>
                    <span class="calendar-entry-desc">{{ entry.description || 'Brak opisu' }}</span>
                  </div>

                  <div class="calendar-entry-meta">
                    <span class="calendar-entry-author">{{ entry.createdByUserName ?? 'Nieznany autor' }}</span>
                    <span class="calendar-entry-date">{{ formatDateTime(entry.createdDate) }}</span>
                    <span *ngIf="entry.editableByCurrentUser === false" class="calendar-entry-lock">
                      Tylko do odczytu
                    </span>
                  </div>
                </button>
              </div>
            </div>
          } @else {
            <div class="calendar-empty-state">
              <i class="pi pi-calendar-times"></i>
              <h4>Brak pozycji na ten dzień</h4>
              <p>Wybierz inny dzień albo dodaj pozycję z poziomu widoku tygodniowego.</p>
            </div>
          }
        </div>
      </p-card>
    </div>
  </ng-container>
</app-page-wrapper>
